/**
 * NFC Solutions — CapGo self-hosted OTA updater (Session 27.63)
 *
 * ═══════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS
 * ═══════════════════════════════════════════════════════════════════
 * Every fix to the React frontend used to require:
 *   1. Push a commit → GitHub Actions builds a fresh APK.
 *   2. Owner downloads the APK from GitHub Releases.
 *   3. Owner side-loads it onto every tablet in every restaurant.
 *
 * With CapGo's `@capacitor/capacitor-updater` plugin, step 2 & 3 go
 * away for React bundle updates (JS + CSS + HTML). The plugin polls
 * `/api/updates/latest` on every app resume, downloads the zip of
 * the new CRA `build/` output, extracts it, and hot-swaps it on the
 * next cold launch. Native code changes (Capacitor plugins, native
 * SDK bumps) still require a fresh APK — but those are rare.
 *
 * ═══════════════════════════════════════════════════════════════════
 * DESIGN CHOICES
 * ═══════════════════════════════════════════════════════════════════
 * • **Manual apply, not auto-apply.** The plugin config in
 *   `capacitor.config.ts` sets `directUpdate=false` so downloaded
 *   updates are staged but NOT applied until the app next relaunches.
 *   This is critical for a POS: yanking the JS bundle from under a
 *   waiter mid-order would be catastrophic. We surface a small toast
 *   ("Update ready — restart to apply") and let the operator choose
 *   when to restart.
 * • **Silent when possible.** If the app has been in the background
 *   for >5 minutes when it comes back to foreground AND an update is
 *   ready, we auto-apply on the assumption the shift is idle. This
 *   is the safe default without needing the user to remember.
 * • **Never in a browser.** OTA updates are meaningless on the web —
 *   the browser always fetches the latest JS from the CDN on reload.
 *   The plugin also throws if called outside Capacitor. We gate
 *   everything on `isInstalledAppEnvironment()`.
 */

import { isInstalledAppEnvironment } from "./device";

// Lazy plugin import — pulling `@capgo/capacitor-updater` at module
// load time forces every browser build to include the plugin bundle
// even though it's never used. Defer to first invocation.
let _CapacitorUpdater = null;
async function _loadPlugin() {
  if (_CapacitorUpdater) return _CapacitorUpdater;
  try {
    const mod = await import("@capgo/capacitor-updater");
    _CapacitorUpdater = mod.CapacitorUpdater || mod.default || null;
    return _CapacitorUpdater;
  } catch (err) {
    console.warn("[ota] Failed to load @capgo/capacitor-updater:", err?.message || err);
    return null;
  }
}

let _armed = false;
let _pendingBundleId = null;
let _pendingVersion = "";
let _listeners = new Set();

/** Register a callback that fires when an update has been downloaded
 * and is ready to apply. Returns an unsubscribe function. */
export function subscribeOtaReady(handler) {
  if (typeof handler !== "function") return () => {};
  _listeners.add(handler);
  // If a bundle is already downloaded (e.g. subscriber mounted late),
  // fire immediately so it sees the current state.
  if (_pendingBundleId) {
    try { handler({ version: _pendingVersion, bundleId: _pendingBundleId }); } catch (_) { /* noop */ }
  }
  return () => { _listeners.delete(handler); };
}

function _fire(evt) {
  for (const h of _listeners) {
    try { h(evt); } catch (_) { /* noop */ }
  }
}

/**
 * Arm the OTA updater. Safe to call multiple times — subsequent
 * invocations are no-ops. Only wires listeners when running inside
 * an installed native shell (Capacitor APK / Tauri EXE).
 *
 * Wire order:
 *   1. `updateAvailable` — plugin discovered a new bundle on the
 *      update server. It will START downloading automatically.
 *   2. `downloadComplete` — bundle downloaded to the app data dir
 *      and staged for next relaunch. Surface UI here.
 *   3. `updateFailed` — download failed OR checksum mismatch.
 *      Silent — the plugin will retry on the next resume.
 */
export async function armOtaUpdater() {
  if (_armed) return;
  try {
    if (!isInstalledAppEnvironment()) return;
    // Session 30.11 — capgo OTA is a Capacitor (Android) plugin only. In
    // the Tauri EXE the web shim rejects every call with "not implemented
    // on web" — skip it there instead of spraying unhandled rejections.
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    if (!cap || typeof cap.isNativePlatform !== "function" || !cap.isNativePlatform()) return;
  } catch (_) { return; }
  const plugin = await _loadPlugin();
  if (!plugin) return;
  _armed = true;

  // 1. Bundle download complete → we have an update staged.
  try {
    Promise.resolve(plugin.addListener("downloadComplete", (info) => {
      _pendingBundleId = info?.bundle?.id || info?.bundleId || "pending";
      _pendingVersion = info?.bundle?.version || info?.version || "";
      console.info("[ota] downloadComplete", { version: _pendingVersion, id: _pendingBundleId });
      _fire({ version: _pendingVersion, bundleId: _pendingBundleId, kind: "download_complete" });
    })).catch(() => { /* plugin not implemented on this shell */ });
  } catch (_) { /* older plugin versions */ }

  // 2. Update is now the current bundle → happens after user taps
  // "Restart to apply" and the app cold-boots into the new bundle.
  try {
    Promise.resolve(plugin.addListener("updateInstalled", (info) => {
      console.info("[ota] updateInstalled", info);
      _pendingBundleId = null;
      _pendingVersion = "";
    })).catch(() => { /* noop */ });
  } catch (_) { /* noop */ }

  // 3. Failure — silent, plugin will retry. We just want the log.
  try {
    Promise.resolve(plugin.addListener("updateFailed", (info) => {
      console.warn("[ota] updateFailed", info);
    })).catch(() => { /* noop */ });
  } catch (_) { /* noop */ }

  // 4. Notify the plugin that the current app instance is running
  // successfully. Without this call, the plugin will roll back to
  // the previous bundle on the next launch (safety net against
  // shipping a broken JS bundle). MUST fire once per cold start.
  try {
    // Small delay so any first-paint crashes have a chance to fire
    // before we "commit" the current bundle as healthy.
    setTimeout(async () => {
      try { await plugin.notifyAppReady(); }
      catch (_) { /* noop */ }
    }, 5000);
  } catch (_) { /* noop */ }
}

/** Apply the currently-staged bundle by cold-restarting the app.
 * No-op if no bundle is pending. */
export async function applyPendingUpdate() {
  if (!_pendingBundleId) return false;
  const plugin = await _loadPlugin();
  if (!plugin) return false;
  try {
    // `reset({ toLastSuccessful: false })` reloads the WebView with
    // the newly-staged bundle. Equivalent to a full cold restart on
    // both Android and iOS. On failure the plugin will auto-revert.
    if (typeof plugin.reload === "function") {
      await plugin.reload();
    } else if (typeof plugin.reset === "function") {
      await plugin.reset({ toLastSuccessful: false });
    } else {
      // Fallback for older plugin versions — just reload the WebView.
      window.location.reload();
    }
    _pendingBundleId = null;
    _pendingVersion = "";
    return true;
  } catch (err) {
    console.warn("[ota] applyPendingUpdate failed:", err?.message || err);
    return false;
  }
}

/** Force an immediate update check — used by the "Check for updates"
 * button in Settings. Returns the plugin's raw response. */
export async function checkForOtaUpdate() {
  const plugin = await _loadPlugin();
  if (!plugin) return { ok: false, reason: "plugin_unavailable" };
  try {
    const info = typeof plugin.getLatest === "function"
      ? await plugin.getLatest()
      : null;
    return { ok: true, info: info || null };
  } catch (err) {
    return { ok: false, reason: err?.message || String(err) };
  }
}

/** Read the current bundle's version — useful for surfacing "v1.1.4
 * (bundle: 2026-01-15)" in Settings. */
export async function getCurrentOtaBundle() {
  const plugin = await _loadPlugin();
  if (!plugin) return null;
  try {
    if (typeof plugin.current === "function") {
      const c = await plugin.current();
      return c?.bundle || c || null;
    }
    return null;
  } catch (_) { return null; }
}
